/**
 * Tone and framing for every blurred-artwork atmosphere in the app.
 *
 * Two facts drive this file:
 *
 * 1. **Blurring preserves chroma; dimming destroys it, proportionally.** A
 *    stage still pushed to ~35% under a dark scrim lands on brown — its hue was
 *    always correct, only its saturation was gone, and brown is precisely dark
 *    desaturated orange. So restore the chroma rather than replacing the
 *    photograph: the blur's soft irregular shape is the atmosphere we want, and
 *    a designed gradient cannot imitate it (tried 2026-07-29, rejected — see
 *    dev-screenshots/2026-07-29-ambient-color/VERDICT.md).
 *
 * 2. **A program cover is type on a flat field, not a photograph.** Cover-
 *    cropping it into a short wide strip magnifies whatever sits mid-frame, and
 *    mid-frame is where every studio puts its title — so a centred anchor makes
 *    the atmosphere a blurred wordmark. Anchoring partway up lands on the
 *    cover's artwork band instead, below the studio logo and above the type.
 *
 * These live together because web paints this atmosphere through an imperative
 * DOM compositor (webPersistentBackdrop.ts) while native paints it through
 * Expo Image, and the two must not drift apart.
 */

/**
 * `background-position` / `contentPosition` Y for the blurred cover.
 *
 * 50 = centred = the original app-wide behaviour, and it must stay the default:
 * the page backdrop is ONE global compositor shared by every route, so changing
 * this changes home — whose brand art is already correct and is not to be
 * touched (Dax, 2026-07-29, twice). The recital page wants 25 (halfway to the
 * top, off its cover's wordmark); that needs a per-route override threaded
 * through PageBackdropProvider, NOT a new global default.
 */
export const ARTWORK_FOCAL_Y = 50;

/**
 * Focal Y for a page whose backdrop is a PROGRAM COVER, opted into per route.
 *
 * Halfway to the top: the cover's artwork band, below the studio logo and above
 * the title. Routes backed by brand art or stage stills must keep the centred
 * default — that is the whole reason this is a per-route value and not a new
 * global.
 */
export const COVER_ARTWORK_FOCAL_Y = 25;

export type PageBackdropArtworkRole = "centered-artwork" | "program-cover";

/**
 * Keep a page backdrop's bitmap and framing role together at the route boundary.
 * A program cover is not merely another URI: every surface that uses it as the
 * page atmosphere must inherit the cover focal anchor as part of that identity.
 */
export function pageBackdropPresentation(
  uri: string | null,
  role: PageBackdropArtworkRole
): { focalY: number | null; uri: string | null } {
  return {
    focalY: role === "program-cover" ? COVER_ARTWORK_FOCAL_Y : null,
    uri
  };
}

/** Card and panel glow (Artwork.tsx `Backdrop`). */
export const CARD_SATURATION = 1.4;

/**
 * The page atmosphere takes NO saturation.
 *
 * A page backdrop is a single large still at 45%, not a card glow crushed under
 * a 0.92 scrim, so its chroma survives intact — a blue-lit number already blurs
 * into a vividly blue room, and saturating it further reads as garish (Dax,
 * 2026-07-29, on the Recital 1 page). The brown problem this file exists to fix
 * is a *card* problem, caused by the card's scrim. Do not "fix" the page too.
 */

/**
 * The page atmosphere's settling scrim: opacity of the canvas laid over the
 * blurred artwork, top and mid.
 *
 * This — not the artwork's brightness — is what makes a page read dark. The
 * artwork sits at 45% and this lays up to fully-opaque canvas on top, so a
 * `brightness()` filter on the image is very nearly invisible (tried
 * 2026-07-29; Dax: "it looks even darker than fucking ever before"). Lift these
 * to lighten the page. The bottom stop stays fully opaque so the strip meets the
 * page canvas without a seam.
 */
export const PAGE_SCRIM_TOP = 0.15;
export const PAGE_SCRIM_MID = 0.55;
export const PAGE_SCRIM_MID_STOP = 55;

/**
 * Dev-only numeric override from the query string, so tone is dialed on the
 * real screen rather than guessed. Layers onto the live app; absent the param,
 * these are the shipped constants.
 */
function devOverride(param: string, fallback: number): number {
  // Deliberately no `react-native` import anywhere in this module:
  // webPersistentBackdrop.ts imports it and is unit-tested in a plain Node
  // environment, where pulling in react-native's Flow source fails to parse.
  // A DOM check identifies web without needing Platform.
  if (typeof document === "undefined" || typeof URLSearchParams === "undefined") {
    return fallback;
  }
  const raw = new URLSearchParams(document.location.search).get(param);
  const parsed = raw != null ? Number.parseFloat(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

export const cardSaturation = () => devOverride("sat", CARD_SATURATION);
/** Resolve a route's requested focal Y, honouring the `?bg=` dev override. */
export const artworkFocalY = (requested?: number | null) =>
  devOverride("bg", requested ?? ARTWORK_FOCAL_Y);
export const pageScrimTop = () => devOverride("scrimtop", PAGE_SCRIM_TOP);
export const pageScrimMid = () => devOverride("scrim", PAGE_SCRIM_MID);

/** The scrim as a top-to-bottom colour ramp, shared by both compositors. */
export function pageScrimStops(): [string, string, string] {
  return [
    `rgba(13, 14, 20, ${pageScrimTop()})`,
    `rgba(13, 14, 20, ${pageScrimMid()})`,
    "rgb(13, 14, 20)"
  ];
}
